import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useStorageSASMutation, useUserStorageSASMutation } from '@foundershub/api-client';
import { FileField } from '@foundershub/components';
import { useAccount } from '../store';
import { AuthError } from '../store/errors';

export interface DocumentFile<Extension extends string> {
  file: File;
  extension: Extension;
  path?: string;
}

interface Props<Extension extends string>
  extends Omit<React.ComponentProps<typeof FileField>, 'accept' | 'onChangeFile' | 'locale'> {
  maxBytes?: number;
  onFileUploaded: (file: DocumentFile<Extension> | null) => void;
  validExtensions?: Array<Extension>;
  fileUploadType?: 'account' | 'user';
}

export function AccountFileField<Extension extends string>({
  maxBytes,
  onFileUploaded,
  validExtensions,
  fileUploadType = 'account',
  ...rest
}: Props<Extension>) {
  const account = useAccount();
  if (fileUploadType === 'account' && !account) {
    throw new AuthError();
  }
  const intl = useIntl();

  const [fileError, setFileError] = React.useState<FileError | void>();
  const [isUploading, setIsUploading] = React.useState(false);

  const [getSASUri] = useStorageSASMutation();
  const [getUserSASUri] = useUserStorageSASMutation();

  const handleChangeFile = React.useCallback(
    async (files: FileList | null) => {
      const file = files?.length ? files[0] : undefined;
      if (!file) {
        setFileError(undefined);
        onFileUploaded(null);
        return;
      }

      const filenameParts = file.name.split('.') || [];
      const fileExt = filenameParts[filenameParts.length - 1] as Extension;
      const isValidFileExt = !validExtensions || validExtensions.includes(fileExt);

      if (!isValidFileExt) {
        setFileError('bad-ext');
        return;
      }

      if (maxBytes && file.size > maxBytes) {
        setFileError('too-large');
        return;
      }

      setFileError(undefined);

      try {
        setIsUploading(true);
        const { sas_uri } =
          fileUploadType === 'account' && account
            ? await getSASUri(account.id).unwrap()
            : await getUserSASUri().unwrap();
        const url = new URL(sas_uri);
        const path = fileUploadType === 'user' ? url.pathname.replace(/^\/|\/$/g, '') : undefined;
        url.pathname = `${url.pathname}/${file.name}`;
        await fetch(url.toString(), {
          method: 'PUT',
          headers: {
            'content-type': file.type,
            'content-length': `${file.size}`,
            'x-ms-blob-type': 'BlockBlob',
          },
          credentials: 'omit',
          body: file,
        });

        onFileUploaded({ file, extension: fileExt, path });
      } catch (e) {
        setFileError('generic');
        onFileUploaded(null);
      } finally {
        setIsUploading(false);
      }
    },
    [onFileUploaded, validExtensions]
  );

  return (
    <FileField
      validationMessage={
        fileError
          ? (intl.formatMessage(fileErrors[fileError], {
              maxFileSize: (maxBytes || 0) / mebibyte,
            }) as string)
          : undefined
      }
      validationState={fileError ? 'error' : undefined}
      {...rest}
      accept={(validExtensions || []).map((ext) => `.${ext}`).join(',')}
      onChangeFile={handleChangeFile}
      isSubmitting={isUploading}
      locale={intl.locale}
    />
  );
}

const mebibyte = 1_048_576;

type FileError = 'too-large' | 'bad-ext' | 'generic';

const fileErrors = defineMessages<FileError>({
  'too-large': {
    defaultMessage: 'File is too large. Please select a file up to {maxFileSize, number, :: ./w unit/megabyte}.',
    description: 'File size too large error message',
  },
  'bad-ext': {
    defaultMessage: 'File type is not accepted. Please select another file.',
    description: 'File type incorrect error message',
  },
  generic: {
    defaultMessage: 'There was an error.',
    description: 'error message',
  },
});
